import React from "react";
import { cn } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Typography, TypographyContent } from "../../atoms";
import { ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { Select as SelectInput, SelectOption, SelectValue } from "../Select";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";

export interface FormFieldSelectProps extends BaseFormFieldProps {
  /**
   * Validation constraints for the field.
   */
  constraints: FormFieldConstraint;
  /**
   * Current value of the select field.
   * Can be a single value or array for multi-select.
   */
  value: SelectValue | null;
  /**
   * Callback function called when the field value changes.
   */
  onChangeField: (id: string, value: SelectValue | null) => void;
  /**
   * Placeholder text shown when no option is selected.
   */
  placeholder?: TypographyContent;
  /**
   * Automatically focuses the field when mounted.
   * @default false
   */
  autoFocus?: boolean;
  /**
   * Array of available options to choose from.
   */
  options: SelectOption[];
  /**
   * Shows a clear button to reset the selection.
   * Automatically disabled for required fields.
   * @default true
   */
  clearable?: boolean;
  /**
   * Enables selection of multiple options.
   * @default false
   */
  multi?: boolean;
  /**
   * Message to display when no options are available.
   */
  noOptions?: TypographyContent;
  /**
   * Message to display when search yields no results.
   */
  noOptionsFor?: TypographyContent;
  /**
   * Applies bold font weight to the selected value.
   * @default false
   */
  strong?: boolean;
}

export const Select: React.FC<FormFieldSelectProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched = false,
  helper = null,
  helperKey,
  constraints,
  placeholder,
  autoFocus,
  disabled,
  keepHeight,
  noLabel,
  dense,
  options,
  clearable = true,
  multi,
  noOptions,
  noOptionsFor,
  readOnly,
}) => {
  const error = touched && errors[0];

  const handleChange = (selectedValue: SelectValue) => {
    onChangeField(id, selectedValue);
  };

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm"
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <SelectInput
        options={options}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        clearable={!readOnly && clearable && !constraints.presence}
        multi={multi}
        noOptions={noOptions}
        noOptionsFor={noOptionsFor}
        dense={dense}
      />
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
    </div>
  );
};
